/* ============================================================================
   password-toggle.js - show/hide toggle for password inputs
   ============================================================================ */

import { fillIcons } from "./fill-icons.js?v=20261007m";

function setToggleState(button, visible) {
  button.setAttribute("aria-pressed", visible ? "true" : "false");
  button.setAttribute("aria-label", visible ? "Hide password" : "Show password");
  const iconEl = button.querySelector("[data-icon]");
  if (iconEl) iconEl.dataset.icon = visible ? "eyeOff" : "eye";
  fillIcons(button);
}

/** Reset inputs in a scope back to masked passwords (e.g. when closing a modal). */
export function resetPasswordVisibility(root = document) {
  root.querySelectorAll(".password-field input").forEach((input) => {
    input.type = "password";
    clearPasswordFieldPaint(input);
  });
  root.querySelectorAll(".password-toggle").forEach((button) => {
    setToggleState(button, false);
  });
}

function clearPasswordFieldPaint(input) {
  if (!input) return;
  input.style.removeProperty("outline");
  input.style.removeProperty("border-color");
  input.style.removeProperty("border");
  input.style.removeProperty("box-shadow");
  input.style.removeProperty("background-color");
  input.style.removeProperty("filter");
}

function paintIdlePasswordField(input) {
  if (!input) return;
  input.style.setProperty("outline", "none", "important");
  input.style.setProperty("border", "1.5px solid var(--border)", "important");
  input.style.setProperty("box-shadow", "0 0 0 1000px var(--canvas-2) inset", "important");
  input.style.setProperty("background-color", "var(--canvas-2)", "important");
  input.style.setProperty("filter", "none", "important");
}

function syncPasswordModalFields(modal) {
  const inputs = modal.querySelectorAll('input[type="password"]');
  const active = modal.contains(document.activeElement) ? document.activeElement : null;
  inputs.forEach((input) => {
    if (input === active) clearPasswordFieldPaint(input);
    else paintIdlePasswordField(input);
  });
}

function schedulePasswordModalSync(modal) {
  syncPasswordModalFields(modal);
  requestAnimationFrame(() => {
    syncPasswordModalFields(modal);
    requestAnimationFrame(() => syncPasswordModalFields(modal));
  });
  window.setTimeout(() => syncPasswordModalFields(modal), 0);
  window.setTimeout(() => syncPasswordModalFields(modal), 50);
  window.setTimeout(() => syncPasswordModalFields(modal), 150);
}

/**
 * Firefox keeps a yellow autofill contour on the idle password field while the
 * password manager targets the other field. CSS cannot override it reliably;
 * re-paint idle siblings after focus moves (and on delayed frames).
 */
export function wirePasswordModalIdleFix(modal) {
  if (!modal || modal.dataset.pwIdleFix === "1") return;
  modal.dataset.pwIdleFix = "1";

  const scheduleSync = () => schedulePasswordModalSync(modal);

  modal.addEventListener("focusin", scheduleSync);
  modal.addEventListener("focusout", () => requestAnimationFrame(scheduleSync));
  modal.addEventListener("animationstart", (e) => {
    if (e.animationName === "ccj-moz-autofill-start") scheduleSync();
  });
  modal.addEventListener("input", (e) => {
    if (e.target.matches('input[type="password"]')) scheduleSync();
  });
  modal.addEventListener("close", () => {
    modal.querySelectorAll('input[type="password"]').forEach(clearPasswordFieldPaint);
  });

  if (!modal.dataset.pwShowModalWrapped) {
    modal.dataset.pwShowModalWrapped = "1";
    const nativeShowModal = modal.showModal.bind(modal);
    modal.showModal = () => {
      nativeShowModal();
      scheduleSync();
    };
  }
}

/** Re-paint password fields after the modal is already open (e.g. after focus()). */
export function repaintPasswordModalFields(modal) {
  if (modal) schedulePasswordModalSync(modal);
}

/** Add an eye toggle beside every password input under `root`. */
export function wirePasswordToggles(root = document) {
  root.querySelectorAll('input[type="password"]').forEach((input) => {
    if (input.dataset.toggleWired === "1") return;
    input.dataset.toggleWired = "1";

    const wrap = document.createElement("div");
    wrap.className = "password-field";
    input.parentNode.insertBefore(wrap, input);
    wrap.appendChild(input);

    const button = document.createElement("button");
    button.type = "button";
    button.className = "password-toggle";
    button.setAttribute("aria-pressed", "false");
    button.setAttribute("aria-label", "Show password");
    button.innerHTML = '<span data-icon="eye"></span>';
    wrap.appendChild(button);

    button.addEventListener("click", () => {
      const show = input.type === "password";
      input.type = show ? "text" : "password";
      setToggleState(button, show);
    });
  });

  fillIcons(root);
}
